/**
 * Session Audit Trail Service
 *
 * Tracks and logs all session-related events for accountability:
 * - Login/logout events
 * - Screen lock/unlock events
 * - Session duration tracking
 * - User activity monitoring
 */

import { db } from '../config/firebase';
import { collection, addDoc, query, where, orderBy, limit, getDocs, Timestamp } from 'firebase/firestore';

export interface SessionEvent {
  id?: string;
  userId: string;
  userEmail: string;
  userName: string;
  userRole: string;
  employeeNumber?: string;
  eventType: 'login' | 'logout' | 'lock' | 'unlock' | 'inactivity_lock';
  timestamp: Date;
  deviceInfo?: {
    userAgent: string;
    platform: string;
    screenResolution: string;
  };
  sessionDuration?: number; // in seconds
  metadata?: Record<string, any>;
}

export interface SessionSummary {
  userId: string;
  userName: string;
  totalSessions: number;
  totalDuration: number; // in seconds
  lastLogin: Date;
  lastLogout?: Date;
  lockCount: number;
  unlockCount: number;
}

class SessionAuditService {
  private collectionName = 'sessionAuditTrail';

  /**
   * Log a session event
   */
  async logEvent(event: Omit<SessionEvent, 'id'>): Promise<string> {
    try {
      const eventData: any = {
        ...event,
        timestamp: Timestamp.fromDate(event.timestamp),
        deviceInfo: event.deviceInfo || this.getDeviceInfo(),
      };

      // Remove undefined fields (Firebase doesn't accept undefined values)
      Object.keys(eventData).forEach(key => {
        if (eventData[key] === undefined) {
          delete eventData[key];
        }
      });

      const docRef = await addDoc(collection(db, this.collectionName), eventData);

      console.log(`[Session Audit] Event logged: ${event.eventType} by ${event.userName}`, {
        eventId: docRef.id,
        timestamp: event.timestamp,
      });

      return docRef.id;
    } catch (error) {
      console.error('[Session Audit] Error logging event:', error);
      throw error;
    }
  }

  /**
   * Log user login
   */
  async logLogin(user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    role: string;
    employeeNumber?: string;
  }): Promise<string> {
    return this.logEvent({
      userId: user.id,
      userEmail: user.email,
      userName: `${user.firstName} ${user.lastName}`,
      userRole: user.role,
      employeeNumber: user.employeeNumber,
      eventType: 'login',
      timestamp: new Date(),
    });
  }

  /**
   * Log user logout
   */
  async logLogout(
    user: {
      id: string;
      email: string;
      firstName: string;
      lastName: string;
      role: string;
      employeeNumber?: string;
    },
    sessionDuration?: number
  ): Promise<string> {
    return this.logEvent({
      userId: user.id,
      userEmail: user.email,
      userName: `${user.firstName} ${user.lastName}`,
      userRole: user.role,
      employeeNumber: user.employeeNumber,
      eventType: 'logout',
      timestamp: new Date(),
      sessionDuration,
    });
  }

  /**
   * Log screen lock (manual or inactivity)
   */
  async logLock(
    user: {
      id: string;
      email: string;
      firstName: string;
      lastName: string;
      role: string;
      employeeNumber?: string;
    },
    isInactivityLock: boolean = false
  ): Promise<string> {
    return this.logEvent({
      userId: user.id,
      userEmail: user.email,
      userName: `${user.firstName} ${user.lastName}`,
      userRole: user.role,
      employeeNumber: user.employeeNumber,
      eventType: isInactivityLock ? 'inactivity_lock' : 'lock',
      timestamp: new Date(),
      metadata: {
        autoLock: isInactivityLock,
      },
    });
  }

  /**
   * Log screen unlock
   */
  async logUnlock(user: {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    role: string;
    employeeNumber?: string;
  }): Promise<string> {
    return this.logEvent({
      userId: user.id,
      userEmail: user.email,
      userName: `${user.firstName} ${user.lastName}`,
      userRole: user.role,
      employeeNumber: user.employeeNumber,
      eventType: 'unlock',
      timestamp: new Date(),
    });
  }

  /**
   * Get recent session events for a user
   */
  async getUserRecentEvents(userId: string, limitCount: number = 20): Promise<SessionEvent[]> {
    try {
      const q = query(
        collection(db, this.collectionName),
        where('userId', '==', userId),
        orderBy('timestamp', 'desc'),
        limit(limitCount)
      );

      const snapshot = await getDocs(q);
      return snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          timestamp: data.timestamp?.toDate() || new Date(),
        } as SessionEvent;
      });
    } catch (error) {
      console.error('[Session Audit] Error fetching user events:', error);
      return [];
    }
  }

  /**
   * Get all session events for a date range
   */
  async getEventsInDateRange(startDate: Date, endDate: Date): Promise<SessionEvent[]> {
    try {
      const q = query(
        collection(db, this.collectionName),
        where('timestamp', '>=', Timestamp.fromDate(startDate)),
        where('timestamp', '<=', Timestamp.fromDate(endDate)),
        orderBy('timestamp', 'desc')
      );

      const snapshot = await getDocs(q);
      return snapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          timestamp: data.timestamp?.toDate() || new Date(),
        } as SessionEvent;
      });
    } catch (error) {
      console.error('[Session Audit] Error fetching events in date range:', error);
      return [];
    }
  }

  /**
   * Get device information
   */
  private getDeviceInfo() {
    return {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      screenResolution: `${window.screen.width}x${window.screen.height}`,
    };
  }

  /**
   * Calculate session summary for a user
   */
  async getUserSessionSummary(userId: string, days: number = 30): Promise<SessionSummary | null> {
    try {
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);

      const events = await this.getEventsInDateRange(startDate, new Date());
      const userEvents = events.filter((e) => e.userId === userId);

      if (userEvents.length === 0) return null;

      const loginEvents = userEvents.filter((e) => e.eventType === 'login');
      const logoutEvents = userEvents.filter((e) => e.eventType === 'logout');
      const lockEvents = userEvents.filter((e) => e.eventType === 'lock' || e.eventType === 'inactivity_lock');
      const unlockEvents = userEvents.filter((e) => e.eventType === 'unlock');

      const totalDuration = logoutEvents.reduce((sum, e) => sum + (e.sessionDuration || 0), 0);

      return {
        userId,
        userName: userEvents[0]?.userName || '',
        totalSessions: loginEvents.length,
        totalDuration,
        lastLogin: loginEvents[0]?.timestamp || new Date(),
        lastLogout: logoutEvents[0]?.timestamp,
        lockCount: lockEvents.length,
        unlockCount: unlockEvents.length,
      };
    } catch (error) {
      console.error('[Session Audit] Error calculating session summary:', error);
      return null;
    }
  }
}

export const sessionAuditService = new SessionAuditService();
